
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageToggle from './LanguageToggle';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileOpen]);

  const navLinks = [
    { name: 'Home', path: '/', labelKey: 'nav.home' },
    { name: 'Records', path: '/records', labelKey: 'nav.records' },
    { name: 'Licensing + Publishing', path: '/licensing-publishing', labelKey: 'nav.licensingPublishing' },
    { name: 'Management', path: '/management', labelKey: 'nav.management' },
    { name: 'Contact', path: '/contact', labelKey: 'nav.contact' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || !isHome ? 'bg-cream shadow-xl border-b-2 border-leather-dark' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex items-center justify-between h-20 lg:h-24">
            <Link to="/" className="flex items-center">
              <img
                src="https://static.readdy.ai/image/f407d1ad302e1be1c54e67247a4ef201/60de7e36a1bed6e668220b00c0ac7762.png"
                alt="Anson Road Records"
                className="h-12 lg:h-16 w-auto"
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-sans text-sm font-bold tracking-wider uppercase transition-colors whitespace-nowrap cursor-pointer ${
                    location.pathname === link.path
                      ? 'text-orange-burnt'
                      : isScrolled || !isHome
                      ? 'text-leather-dark hover:text-orange-burnt'
                      : 'text-cream hover:text-orange-burnt'
                  }`}
                >
                  {t(link.labelKey)}
                </Link>
              ))}
              <a
                href="https://ranchfireradio.com"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className={`text-sm uppercase tracking-wider transition-colors cursor-pointer font-bold whitespace-nowrap ${
                  isScrolled || !isHome
                    ? 'text-leather-dark hover:text-orange-burnt'
                    : 'text-cream hover:text-orange-burnt'
                }`}
              >
                RanchFire Radio
              </a>
              <LanguageToggle variant={isScrolled || !isHome ? 'dark' : 'light'} />
            </div>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer transition-colors ${
                isScrolled || !isHome ? 'text-leather-dark' : 'text-cream'
              }`}
              aria-label="Toggle menu"
            >
              <i className={`${isMobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Overlay */}
      <div
        className={`fixed inset-0 bg-black/60 z-40 transition-opacity duration-300 lg:hidden ${
          isMobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMobileOpen(false)}
      ></div>

      {/* Mobile Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-72 bg-leather-dark z-50 transition-transform duration-300 lg:hidden ${
          isMobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between p-5 border-b border-cream/20">
          <img
            src="https://static.readdy.ai/image/f407d1ad302e1be1c54e67247a4ef201/60de7e36a1bed6e668220b00c0ac7762.png"
            alt="Anson Road Records"
            className="h-10 w-auto"
          />
          <button
            onClick={() => setIsMobileOpen(false)}
            className="w-10 h-10 flex items-center justify-center text-cream cursor-pointer"
            aria-label="Close menu"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>
        <div className="flex flex-col py-6">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-6 py-4 font-sans text-sm font-bold tracking-wider uppercase transition-colors cursor-pointer ${
                location.pathname === link.path
                  ? 'text-orange-burnt bg-cream/5'
                  : 'text-cream hover:text-orange-burnt hover:bg-cream/5'
              }`}
            >
              {t(link.labelKey)}
            </Link>
          ))}
          <a
            href="https://ranchfireradio.com"
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="px-6 py-4 font-sans text-sm font-bold tracking-wider uppercase text-cream hover:text-orange-burnt hover:bg-cream/5 transition-colors cursor-pointer"
          >
            RanchFire Radio
          </a>
          <div className="px-6 pt-4">
            <LanguageToggle variant="light" />
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;
